/**
 * @author: lv.chen
 * @date: 2026/3/13
 * @desc: 单选下拉选择组件，支持搜索、删除选项、清空等功能
 */
import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Select as TeaSelect, Modal, Button } from 'tea-component';
import Bubble from '@intelligence/v3/components/Bubble';
import EllipsisHover from '@home/components/EllipsisHover';
import SvgIcon from '@home/components/SvgIcon';
import { isEmpty, isNotEmpty, formatText } from '@intelligence/v3/utils/common';

import type { SelectProps as TeaSelectProps, SelectOptionWithGroup as TeaSelectOptionWithGroup } from 'tea-component';

import './index.scss';

/** 选项数据类型 */
export interface ISelectOptionWithGroup extends TeaSelectOptionWithGroup {
  /** 选项图标 */
  icon?: React.ReactNode;
}

export interface ISelectProps extends IBaseProps, TeaSelectProps {
  /** 是否在重复选择同一个选项时，不触发onChange回调，默认true */
  isDistinct?: boolean;
  /** 是否显示按钮内的label名称，默认true */
  isShowLabel?: boolean;
  /** 下拉按钮内的名称（label） */
  label?: string | React.ReactNode;
  /** 气泡提示最大宽度，默认500px */
  tooltipMaxWidth?: number | string;
  /** 选项文本超出省略时，是否通过 EllipsisHover 展示完整文案 */
  optionTextEllipsisHover?: boolean;
  /** 下拉选项数据 */
  options: ISelectOptionWithGroup[];
  /** 筛选器button renderer */
  button?: (selectedOption?: ISelectOptionWithGroup | null) => React.ReactNode;
  /** 清空选项的回调 */
  onClear?: (value: string) => void;
  // === 新增属性 ===
  /** 是否支持搜索 */
  searchable?: boolean;
  /** 搜索占位符 */
  searchPlaceholder?: string;
  /** 无数据文案 */
  emptyText?: string;
  /** 是否自动清空搜索值 */
  autoClearSearchValue?: boolean;
  /** 是否支持删除选项 */
  deletable?: boolean;
  /** 删除选项的回调 */
  onDelete?: (value: string) => void;
  /** 删除确认弹窗标题 */
  deleteConfirmTitle?: string;
  /** 删除确认弹窗描述 */
  deleteConfirmDesc?: string;
  /** 最大显示数量，超过后显示提示 */
  maxDisplayCount?: number;
  /** 自定义底部内容 */
  footer?: React.ReactNode;
  /** 下拉面板宽度 */
  listWidth?: number;
  /** 下拉面板高度 */
  listHeight?: number;
}

/** 清除按钮组件 */
const ClearIcon: React.FC<{ onClick: (e: React.MouseEvent) => void }> = ({ onClick }) => (
  <span className="dbi-component-select-button__clear" onClick={onClick}>
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
      <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
    </svg>
  </span>
);

/** 删除图标组件 */
const DeleteIcon: React.FC<{ onClick: (e: React.MouseEvent) => void }> = ({ onClick }) => (
  <Bubble dark arrowPointAtCenter content="">
    <i className="dbi-component-select-option__delete" onClick={onClick}>
      <SvgIcon type="clear-icon" />
    </i>
  </Bubble>
);

const Select: React.FC<ISelectProps> = (props) => {
  const {
    style,
    className,
    overlayClassName,
    boxClassName,
    disabled = false,
    clearable = false,
    matchButtonWidth = true,
    isDistinct = true,
    isShowLabel = true,
    label = '',
    size = 'm',
    appearance = 'button',
    // 新增属性
    searchable = false,
    searchPlaceholder,
    emptyText,
    autoClearSearchValue = true,
    deletable = false,
    onDelete,
    deleteConfirmTitle,
    deleteConfirmDesc,
    maxDisplayCount,
    listWidth,
    listHeight,
    footer,
    placeholder = 'dbi_please_select',
    tooltipMaxWidth = 500,
    optionTextEllipsisHover = false,
    button,
    options = [],
    tips,
    value,
    onChange,
    onClear,
    ...restProps
  } = props;

  const { locale, formatMessage } = useIntl();

  // 删除确认弹窗状态
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string>('');

  // 处理label文本
  const labelText = formatText(formatMessage, label) as string;

  // 处理placeholder文本
  const placeHolderText = formatText(formatMessage, placeholder) as string;

  // 处理空态文案
  const emptyTipsText = useMemo(() => {
    if (isEmpty(emptyText)) {
      return undefined;
    }

    return formatText(formatMessage, emptyText);
  }, [emptyText, formatMessage]);

  // 是否显示 footer 提示
  const showFooter = useMemo(() => {
    if (footer !== undefined) {
      return footer;
    }
    if (maxDisplayCount !== undefined && options.length >= maxDisplayCount) {
      return (
        <div className="dbi-component-select-footer">{formatMessage({ id: 'dbi_filterSave_my_filter_top10' })}</div>
      );
    }
    return undefined;
  }, [footer, maxDisplayCount, options.length, formatMessage]);

  // 渲染选项文本（带图标和删除按钮）
  const renderOptionContent = (option: ISelectOptionWithGroup, showDelete: boolean) => {
    const text = option?.text || '';
    const icon = option?.icon || '';
    const displayText = formatText(formatMessage, text);
    let iconElement = null;

    // 如果有图标
    if (icon) {
      if (typeof icon === 'string') {
        iconElement = <img className="dbi-component-select-option__icon" src={icon} alt="" />;
      } else {
        iconElement = <span className="dbi-component-select-option__icon">{icon}</span>;
      }
    }

    return (
      <div className="dbi-component-select-option">
        {iconElement && iconElement}
        <span className="dbi-component-select-option__text">
          {optionTextEllipsisHover && typeof displayText === 'string' ? (
            <EllipsisHover className="dbi-component-select-option__ellipsis-text" text={displayText} />
          ) : (
            displayText
          )}
        </span>
        {showDelete && deletable && (
          <DeleteIcon
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDeleteTarget(option.value as string);
              setDeleteModalVisible(true);
            }}
          />
        )}
      </div>
    );
  };

  // 处理选项数据，添加tooltip和删除按钮
  const processedOptions = useMemo<ISelectOptionWithGroup[]>(() => {
    if (isEmpty(options)) {
      return [];
    }

    return options.map((option) => {
      const optionTooltip = option?.tooltip;
      const content = renderOptionContent(option, true);
      let optionText: React.ReactNode = content;

      // 如果有tooltip
      if (optionTooltip) {
        const tooltipContent = formatText(formatMessage, optionTooltip);
        optionText = (
          <Bubble maxWidth={tooltipMaxWidth} content={tooltipContent}>
            <div style={{ height: '100%' }}>{renderOptionContent(option, true)}</div>
          </Bubble>
        );
      }

      return {
        ...option,
        text: optionText,
      };
    });
  }, [locale, tooltipMaxWidth, options, deletable, optionTextEllipsisHover]);

  // 自定义按钮渲染
  const buttonRender = (_item: any) => {
    const selectedOption = options.find((option) => option?.value === value) || null;

    if (typeof button === 'function') {
      return button(selectedOption);
    }

    const displayText = selectedOption ? renderOptionContent(selectedOption, false) : placeHolderText;

    return (
      <div className="dbi-component-select-button">
        {isShowLabel && labelText && <span className="dbi-component-select-button__label">{labelText} </span>}
        <span className="dbi-component-select-button__value">{displayText}</span>
        {clearable && value && !disabled && (
          <ClearIcon
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onChange?.('', {} as unknown as any);
              onClear?.(value as string);
            }}
          />
        )}
      </div>
    );
  };

  // 处理删除确认
  const handleDeleteConfirm = () => {
    onDelete?.(deleteTarget);
    // 如果删除的是当前选中的选项，清空选中状态
    if (value === deleteTarget) {
      onChange?.('', {} as unknown as any);
    }
    setDeleteModalVisible(false);
    setDeleteTarget('');
  };

  // 关闭删除弹窗
  const handleDeleteModalClose = () => {
    setDeleteModalVisible(false);
    setDeleteTarget('');
  };

  return (
    <>
      <TeaSelect
        {...restProps}
        style={style}
        className={classnames('dbi-component-select', className, {
          'dbi-component-select--has-selected-value': isNotEmpty(value),
        })}
        overlayClassName={classnames('dbi-component-select-overlay', overlayClassName)}
        boxClassName={classnames('dbi-component-select-box', boxClassName)}
        matchButtonWidth={matchButtonWidth}
        size={size}
        appearance={appearance}
        placeholder={placeHolderText}
        disabled={disabled}
        searchable={searchable}
        searchPlaceholder={searchPlaceholder ? (formatText(formatMessage, searchPlaceholder) as string) : undefined}
        autoClearSearchValue={autoClearSearchValue}
        listWidth={listWidth}
        listHeight={listHeight}
        footer={showFooter}
        tips={tips ?? (isEmpty(processedOptions) ? emptyTipsText : undefined)}
        button={buttonRender}
        options={processedOptions}
        value={value}
        onChange={(payload, context) => {
          if (isDistinct && payload === value) {
            return;
          }

          onChange?.(payload, context);
        }}
      />
      {/* 删除确认弹窗 */}
      {deletable && (
        <Modal visible={deleteModalVisible} onClose={handleDeleteModalClose}>
          <Modal.Body>
            <div className="dbi-component-select-delete-modal__title">
              {deleteConfirmTitle || formatMessage({ id: 'dbi_filterSave_delete_info' })}
            </div>
            <div className="dbi-component-select-delete-modal__desc">
              {deleteConfirmDesc || formatMessage({ id: 'dbi_filterSave_do_delete' })}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button type="primary" onClick={handleDeleteConfirm}>
              {formatMessage({ id: 'common_confirm' })}
            </Button>
            <Button type="weak" onClick={handleDeleteModalClose}>
              {formatMessage({ id: 'common_cancel' })}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default Select;
